import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Buildings, Link as LinkIcon, Plus, Sparkle, X } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { EasyStayConnect } from "@/components/EasyStayConnect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const PROPERTY_TYPES = ["Lodge", "Hotel", "Guest House", "Bed & Breakfast", "Apartments", "Motel", "Hostel", "Event Centre", "Conference Room", "Other"];
const SINGLE_UNIT = new Set(["Apartments", "Other"]);
const STANDARD_TYPES = ["Standard", "Deluxe", "Suite", "Family Suite"];
const SUBTYPE_PRESETS = {
  "Event Centre": ["Main Hall", "Banquet Room", "Outdoor Venue", "VIP Lounge"],
  "Conference Room": ["Boardroom", "Training Room", "Auditorium", "Breakout Room"],
};

export default function Onboarding() {
  const navigate = useNavigate();
  const { refreshSettings } = useCurrency();
  const [base, setBase] = useState(null);
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(null);
  const [prefilled, setPrefilled] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [form, setForm] = useState({ property_name: "", property_type: "", address: "" });
  const [roomTypes, setRoomTypes] = useState([{ name: "Standard", count: "1", custom: false, customName: "" }]);

  const multi = !!form.property_type && !SINGLE_UNIT.has(form.property_type);
  const totalRooms = useMemo(
    () => roomTypes.reduce((s, rt) => s + (parseInt(rt.count, 10) || 0), 0),
    [roomTypes]
  );

  useEffect(() => {
    api.get("/settings").then(({ data }) => {
      setBase(data);
      setForm({
        property_name: data.property_name || "",
        property_type: data.property_type || "",
        address: data.address || "",
      });
    }).catch(() => setBase({}));
    // Hosts arriving from EasyStay's upsell flow: pre-fill from their listing — no re-typing
    api.get("/onboarding/prefill").then(({ data }) => {
      if (data?.found) {
        setPrefilled(true);
        setForm((f) => ({
          property_name: !f.property_name || f.property_name === "EasyStay Lodge" ? data.property_name : f.property_name,
          property_type: f.property_type || data.property_type || "",
          address: f.address || data.address || "",
        }));
        if (data.easystay?.id) setSelected(data.easystay);
      }
    }).catch(() => {});
    api.get("/subscription/pricing").then(({ data }) => setPricing(data)).catch(() => setPricing({ available: false }));
  }, []);

  const updateRow = (i, patch) => setRoomTypes((rows) => rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const addRow = () => setRoomTypes((rows) => [...rows, { name: "Standard", count: "1", custom: false, customName: "" }]);
  const removeRow = (i) => setRoomTypes((rows) => rows.filter((_, j) => j !== i));

  const validRoomTypes = roomTypes
    .map((rt) => ({ name: rt.custom ? rt.customName.trim() : rt.name, count: parseInt(rt.count, 10) || 0 }))
    .filter((rt) => rt.name && rt.count > 0);
  const step1Valid = form.property_name.trim() && (!multi || validRoomTypes.length > 0);

  const finish = async (withConnect) => {
    setSaving(true);
    try {
      const { data } = await api.post("/onboarding/complete", {
        property_name: form.property_name.trim(),
        property_type: form.property_type,
        address: form.address.trim(),
        room_types: multi ? validRoomTypes : [],
        easystay_property_id: withConnect && selected ? selected.id : (base?.easystay_property_id || ""),
        easystay_property_name: withConnect && selected ? selected.name : (base?.easystay_property_name || ""),
        easystay_property_address: withConnect && selected
          ? [selected.address, selected.city].filter(Boolean).join(", ")
          : (base?.easystay_property_address || ""),
      });
      await refreshSettings();
      toast.success(
        withConnect && selected
          ? `Connected to "${selected.name}" — you're all set`
          : data.rooms_created
            ? `Welcome! ${data.rooms_created} room${data.rooms_created === 1 ? "" : "s"} created and ready`
            : "Welcome to EasyStay Manager"
      );
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (base === null) {
    return (
      <div className="flex h-screen items-center justify-center bg-background" data-testid="onboarding-loading">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-10" data-testid="onboarding-page">
      <div className="w-full max-w-lg">
        <div className="mb-8 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/logo.png" alt="EasyStay Manager" className="h-9 w-9 rounded-sm object-cover" data-testid="onboarding-logo-icon" />
            <div className="font-heading text-lg font-bold">EasyStay <span className="text-primary">Manager</span></div>
          </div>
          <div className="flex items-center gap-1.5" data-testid="onboarding-progress">
            {[1, 2].map((n) => (
              <div key={n} className={`h-1.5 w-10 rounded-full transition-colors ${step >= n ? "bg-primary" : "bg-border"}`} />
            ))}
          </div>
        </div>

        {step === 1 && (
          <div className="border border-border bg-card p-6 sm:p-8" data-testid="onboarding-step-property">
            <div className="flex h-11 w-11 items-center justify-center rounded-sm bg-primary/10">
              <Buildings size={22} className="text-primary" />
            </div>
            <h1 className="mt-4 font-heading text-2xl font-bold">Set up your property</h1>
            <p className="mt-1 text-sm text-muted-foreground">A few basics and your front desk is ready. You can change everything later in Settings.</p>
            {prefilled && (
              <div className="mt-4 flex items-center gap-2 border border-primary/40 bg-primary/5 px-3 py-2 text-xs text-foreground" data-testid="onboarding-prefill-note">
                <Sparkle size={14} className="shrink-0 text-primary" />
                Pre-filled from your EasyStay listing — just confirm the details.
              </div>
            )}
            <div className="mt-6 space-y-4">
              <div className="space-y-1.5">
                <Label>Property name</Label>
                <Input value={form.property_name} onChange={(e) => setForm({ ...form, property_name: e.target.value })}
                  placeholder="e.g. Lake View Lodge" data-testid="onboarding-property-name" autoFocus />
              </div>
              <div className="space-y-1.5">
                <Label>Property type</Label>
                <Select value={form.property_type} onValueChange={(v) => {
                  setForm({ ...form, property_type: v });
                  const presets = SUBTYPE_PRESETS[v];
                  if (presets) {
                    setRoomTypes(presets.map((name) => ({ name, count: "1", custom: false, customName: "" })));
                  } else {
                    setRoomTypes((rows) => {
                      const cleaned = rows.filter((r) => r.custom || STANDARD_TYPES.includes(r.name));
                      return cleaned.length ? cleaned : [{ name: "Standard", count: "1", custom: false, customName: "" }];
                    });
                  }
                }}>
                  <SelectTrigger data-testid="onboarding-property-type"><SelectValue placeholder="Choose a type…" /></SelectTrigger>
                  <SelectContent>
                    {PROPERTY_TYPES.map((t) => <SelectItem key={t} value={t} data-testid={`onboarding-type-${t.toLowerCase().replace(/[^a-z]/g, "-")}`}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Address</Label>
                <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                  placeholder="Street, area, city" data-testid="onboarding-address" />
              </div>

              {multi && (
                <div className="space-y-3" data-testid="onboarding-room-types">
                  <Label>{SUBTYPE_PRESETS[form.property_type] ? "Sub-types & number of spaces per sub-type" : "Room types & number of rooms per type"}</Label>
                  {roomTypes.map((rt, i) => (
                    <div key={i} className="flex items-center gap-2" data-testid={`room-type-row-${i}`}>
                      <Select
                        value={rt.custom ? "__custom" : rt.name}
                        onValueChange={(v) => updateRow(i, v === "__custom" ? { custom: true } : { name: v, custom: false, customName: "" })}
                      >
                        <SelectTrigger className="min-w-36" data-testid={`room-type-name-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {(SUBTYPE_PRESETS[form.property_type] || STANDARD_TYPES).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                          <SelectItem value="__custom" data-testid={`room-type-custom-option-${i}`}>Custom type…</SelectItem>
                        </SelectContent>
                      </Select>
                      {rt.custom && (
                        <Input placeholder="Type name" value={rt.customName}
                          onChange={(e) => updateRow(i, { customName: e.target.value })}
                          data-testid={`room-type-custom-${i}`} className="min-w-28" />
                      )}
                      <Input type="number" min="1" className="w-20" value={rt.count}
                        onChange={(e) => updateRow(i, { count: e.target.value })}
                        data-testid={`room-type-count-${i}`} title="Rooms of this type" />
                      {roomTypes.length > 1 && (
                        <Button type="button" size="icon" variant="ghost" onClick={() => removeRow(i)} data-testid={`room-type-remove-${i}`}>
                          <X size={14} />
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button type="button" variant="outline" size="sm" onClick={addRow} data-testid="room-type-add">
                    <Plus size={14} className="mr-1" /> Add room type
                  </Button>
                  <p className="text-xs text-muted-foreground" data-testid="onboarding-rooms-total">
                    {totalRooms} room{totalRooms === 1 ? "" : "s"} in total — we&apos;ll create them for you, numbered from 101.
                  </p>
                </div>
              )}
              {form.property_type && !multi && (
                <p className="text-xs text-muted-foreground" data-testid="onboarding-single-unit-note">
                  Single-unit property — we&apos;ll set it up as one listing with no room types.
                </p>
              )}

              <Button className="w-full transition-colors" disabled={!step1Valid}
                onClick={() => setStep(2)} data-testid="onboarding-continue">
                Continue <ArrowRight size={15} className="ml-1.5" />
              </Button>
              <p className="text-center text-xs text-muted-foreground" data-testid="onboarding-pricing-note">
                30-day free trial included
                {pricing?.available ? ` — then K${pricing.monthly}/month or K${pricing.yearly}/year` : pricing ? " — Contact us for pricing" : ""}
              </p>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="border border-border bg-card p-6 sm:p-8" data-testid="onboarding-step-connect">
            <div className="flex h-11 w-11 items-center justify-center rounded-sm bg-accent/15">
              <LinkIcon size={22} className="text-accent" />
            </div>
            <h1 className="mt-4 font-heading text-2xl font-bold">Connect to EasyStay for automatic relisting</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Optional — link your listing on easystayzm.com so rooms relist automatically when they become vacant and bookings sync both ways.
              EasyStay Manager works fully without this, and you can connect later from Settings.
            </p>
            <div className="mt-6">
              <EasyStayConnect selectedId={selected?.id} onSelect={setSelected} />
            </div>
            <div className="mt-6 flex flex-col gap-2 sm:flex-row">
              <Button variant="outline" onClick={() => setStep(1)} disabled={saving} data-testid="onboarding-back" className="transition-colors">
                <ArrowLeft size={15} className="mr-1.5" /> Back
              </Button>
              <Button variant="ghost" className="sm:ml-auto" onClick={() => finish(false)} disabled={saving} data-testid="onboarding-skip-connect">
                Skip for now
              </Button>
              <Button onClick={() => finish(true)} disabled={!selected || saving} data-testid="onboarding-connect-finish" className="transition-colors">
                {saving ? "Saving…" : selected ? `Connect "${selected.name}"` : "Connect & finish"}
              </Button>
            </div>
            <p className="mt-4 text-center text-xs text-muted-foreground">
              30-day free trial included
              {pricing?.available ? ` — then K${pricing.monthly}/month or K${pricing.yearly}/year` : pricing ? " — Contact us for pricing" : ""}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
